<template>
  <div class="location-picker">
    <!-- Country selection -->
    <div v-if="persistentCountryLabel" class="location-picker__country-label" data-test="location-country-label">
      Country<span v-if="required" class="location-picker__required">*</span>
    </div>
    <CountryPickerField
      :value="selectedCountry"
      :countries="countriesWithFlags"
      :loading="loading && currentSearchLevel === 'country'"
      :search-label="persistentCountryLabel ? '' : countryLabel"
      select-placeholder="Select a country first. Location scope options appear once a country is chosen."
      search-placeholder="Type to search. Location scope options appear once a country is chosen."
      :disabled="disabled || countryDisabled"
      @input="onCountrySelect"
      @search="onSearch('country', $event)"
      @clear="onClear('country')"
    />

    <!-- Single operating country helper text (ER-11872) -->
    <div
      v-if="singleCountryMode"
      class="location-picker__hint location-picker__single-country-hint"
    >
      There is only one operating country in this account
    </div>

    <!-- Country scope toggle (Country-wide / Specific location) -->
    <div
      v-if="selectedCountry && countryConfig && hasScoping"
      class="location-picker__scope-toggle"
    >
      <v-btn-toggle
        v-model="countryScope"
        mandatory
        class="location-picker__toggle"
        :disabled="disabled"
      >
        <v-btn
          value="country-wide"
          class="location-picker__toggle-btn"
          small
          text
        >
          Country-wide
        </v-btn>
        <v-btn
          value="specific"
          class="location-picker__toggle-btn"
          small
          text
        >
          Specific location
        </v-btn>
      </v-btn-toggle>

      <!-- Country-wide hint -->
      <div
        v-if="countryScope === 'country-wide'"
        class="location-picker__hint"
      >
        Pay Range applicable country wide. Switch to Specific location to scope by
        {{ countryConfig.regionLabel?.toLowerCase() || 'region' }}.
      </div>
    </div>

    <!-- Unsupported country hint -->
    <div
      v-if="selectedCountry && countryConfig && !hasScoping"
      class="location-picker__hint"
    >
      Pay Range applies country wide. Location scoping isn't available for this country yet.
    </div>

    <!-- Region selection -->
    <div
      v-if="selectedCountry && countryScope === 'specific' && countryConfig?.hasRegions"
      class="location-picker__level"
    >
      <SearchableLocationField
        :value="selectedRegion"
        :items="regionItems"
        :label="required ? countryConfig.regionLabel + '*' : countryConfig.regionLabel"
        :placeholder="'Search ' + countryConfig.regionLabel?.toLowerCase()"
        :hint="'Type to search. Clearing ' + countryConfig.regionLabel + ' falls back to country-wide.'"
        :loading="loading && currentSearchLevel === 'region'"
        :disabled="disabled"
        @input="onRegionSelect"
        @search="onSearch('region', $event)"
        @clear="onClear('region')"
      />
    </div>

    <!-- Sub-region scope toggle (Metro Area / County) -->
    <div
      v-if="selectedCountry && showSubRegionScope"
      class="location-picker__scope-toggle"
    >
      <v-btn-toggle
        v-model="regionScope"
        mandatory
        class="location-picker__toggle"
        :disabled="disabled"
      >
        <v-btn
          v-if="countryConfig.hasRegions"
          value="region-wide"
          class="location-picker__toggle-btn"
          small
          text
        >
          {{ countryConfig.regionLabel }}-wide
        </v-btn>
        <v-btn
          v-if="countryConfig.metroLabel"
          value="metro"
          class="location-picker__toggle-btn"
          small
          text
        >
          {{ countryConfig.metroLabel }}
        </v-btn>
        <v-btn
          v-if="countryConfig.localUnitLabel"
          value="county"
          class="location-picker__toggle-btn"
          small
          text
        >
          {{ countryConfig.localUnitLabel }}
        </v-btn>
      </v-btn-toggle>

      <!-- Region-wide hint -->
      <div
        v-if="regionScope === 'region-wide'"
        class="location-picker__hint"
      >
        Pay Range applicable {{ countryConfig.regionLabel?.toLowerCase() }} wide.
        Switch to {{ countryConfig.metroLabel }} or {{ countryConfig.localUnitLabel }} to scope deeper.
      </div>
    </div>

    <!-- Sub-region selection (Metro Area or County) -->
    <div
      v-if="selectedCountry && showSubRegionScope && activeSubRegionType"
      class="location-picker__level"
    >
      <SearchableLocationField
        v-if="activeSubRegionType === 'metro'"
        :value="selectedSubRegion"
        :items="subRegionItems"
        :label="required ? countryConfig.metroLabel + '*' : countryConfig.metroLabel"
        :placeholder="'Search ' + countryConfig.metroLabel?.toLowerCase()"
        :hint="getSubRegionHint('metro')"
        :loading="loading && currentSearchLevel === 'subregion'"
        :disabled="disabled"
        @input="onSubRegionSelect"
        @search="onSearch('subregion', $event)"
        @clear="onClear('subregion')"
      />

      <SearchableLocationField
        v-if="activeSubRegionType === 'county'"
        :value="selectedSubRegion"
        :items="subRegionItems"
        :label="required ? countryConfig.localUnitLabel + '*' : countryConfig.localUnitLabel"
        :placeholder="'Search ' + countryConfig.localUnitLabel?.toLowerCase()"
        :hint="getSubRegionHint('county')"
        :loading="loading && currentSearchLevel === 'subregion'"
        :disabled="disabled"
        @input="onSubRegionSelect"
        @search="onSearch('subregion', $event)"
        @clear="onClear('subregion')"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "LocationPicker",
};

import { DEFAULT_COUNTRY_CONFIGS } from "./locationCountryConfigs.js";

// Re-export for FairPayPage / PayRangeModal (ER-11872).
export { DEFAULT_COUNTRY_CONFIGS };
</script>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import CountryPickerField from "@components/CountryPickerField/CountryPickerField.vue";
import SearchableLocationField from "./SearchableLocationField.vue";
import { resolveCountryFlagIcon } from "@components/utils/countryFlagIcon";
import { DEFAULT_COUNTRY_CONFIGS } from "./locationCountryConfigs.js";

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  countries: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Show the "Country" label above the field in every state, not only while searching. */
  persistentCountryLabel: {
    type: Boolean,
    default: false,
  },
  /** Search results for the region level (from parent `@search` handler). */
  regionItems: {
    type: Array,
    default: () => [],
  },
  /** Search results for metro / county level (from parent `@search` handler). */
  subRegionItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue", "search", "clear"]);

const countryLabel = computed(() => (props.required ? "Country*" : "Country"));

const COUNTRY_WIDE = "country-wide";
const SPECIFIC = "specific";
const REGION_WIDE = "region-wide";
const SUB_REGION_SCOPES = ["metro", "county"];

// Initialize internal state from modelValue prop
function getInitialCountry() {
  if (props.modelValue?.countryCode && props.countries) {
    return props.countries.find((c) => c.code === props.modelValue.countryCode) || null;
  }
  return null;
}

function getInitialCountryScope() {
  const scope = props.modelValue?.scope;
  return scope && scope !== COUNTRY_WIDE ? SPECIFIC : COUNTRY_WIDE;
}

// A country without a region level (Romania) starts with no sub-region scope chosen,
// so picking "Specific location" reveals the Metro Area / County toggle unselected.
function initialRegionScope(country) {
  const scope = props.modelValue?.scope;
  if (SUB_REGION_SCOPES.includes(scope)) return scope;
  return country?.hasSubRegions && country.hasRegions ? REGION_WIDE : null;
}

function toLocationOption(id, name, code) {
  if (id == null || id === "") return null;
  const option = { id, name: name || String(id) };
  if (code) option.code = code;
  return option;
}

// Internal state
const selectedCountry = ref(getInitialCountry());
const selectedRegion = ref(
  toLocationOption(
    props.modelValue?.regionId,
    props.modelValue?.regionName,
    props.modelValue?.regionCode
  )
);
const selectedSubRegion = ref(
  toLocationOption(props.modelValue?.subRegionId, props.modelValue?.subRegionName)
);
const countryScope = ref(getInitialCountryScope());
const regionScope = ref(initialRegionScope(getInitialCountry()));
const currentSearchLevel = ref(null);

// Single operating country — auto-select it and lock only the Country field,
// leaving the scope toggle and any region/metro levels interactive (ER-11872).
const countriesWithFlags = computed(() =>
  (props.countries || []).map((country) => ({
    ...country,
    icon: resolveCountryFlagIcon(country),
  }))
);

const singleCountryMode = computed(
  () => Array.isArray(props.countries) && props.countries.length === 1
);
const countryDisabled = computed(() => singleCountryMode.value);

// Country configuration
const countryConfig = computed(() => {
  if (!selectedCountry.value || !props.countries) return null;
  return props.countries.find((c) => c.code === selectedCountry.value.code);
});

// Geography levels the chosen country supports. A country with neither a region nor a
// sub-region level is unsupported and gets the country field plus an explanatory hint.
const hasRegionLevel = computed(() => Boolean(countryConfig.value?.hasRegions));
const hasSubRegionLevel = computed(() => Boolean(countryConfig.value?.hasSubRegions));
const hasScoping = computed(() => hasRegionLevel.value || hasSubRegionLevel.value);

// The sub-region scope toggle shows once the region is known: the selected region for a
// country with a region level, or straight after "Specific location" for one without.
const showSubRegionScope = computed(() => {
  if (!hasSubRegionLevel.value || countryScope.value !== SPECIFIC) return false;
  return hasRegionLevel.value ? Boolean(selectedRegion.value) : true;
});

// Which sub-region level the toggle currently points at — "region-wide" (or nothing) means
// no sub-region field yet.
const activeSubRegionType = computed(() =>
  SUB_REGION_SCOPES.includes(regionScope.value) ? regionScope.value : null
);

// Handlers
function onCountrySelect(country) {
  if (!country) {
    onClear("country");
    return;
  }
  selectedCountry.value = country;
  // Choosing a country resets everything below it.
  selectedRegion.value = null;
  selectedSubRegion.value = null;
  countryScope.value = COUNTRY_WIDE;
  regionScope.value = initialRegionScope(country);
  emitValue();
}

function onRegionSelect(region) {
  selectedRegion.value = region;
  // Reset sub-region
  selectedSubRegion.value = null;
  regionScope.value = REGION_WIDE;
  emitValue();
}

function onSubRegionSelect(subRegion) {
  selectedSubRegion.value = subRegion;
  emitValue();
}

function onClear(level) {
  if (level === "country") {
    selectedCountry.value = null;
    selectedRegion.value = null;
    selectedSubRegion.value = null;
    countryScope.value = COUNTRY_WIDE;
    regionScope.value = null;
  } else if (level === "region") {
    selectedRegion.value = null;
    selectedSubRegion.value = null;
    regionScope.value = REGION_WIDE;
  } else if (level === "subregion") {
    selectedSubRegion.value = null;
    // Clearing the sub-region falls back to region-wide, where the country has one.
    if (hasRegionLevel.value) regionScope.value = REGION_WIDE;
  }
  emit("clear", level);
  emitValue();
}

function onSearch(level, query) {
  currentSearchLevel.value = level;
  emit("search", {
    level,
    query,
    // Parent handlers must not rely on v-model having flushed yet (Pay Range modal).
    countryCode: selectedCountry.value?.code ?? null,
    regionCode: selectedRegion.value?.code ?? null,
  });
}

function emitValue() {
  if (!selectedCountry.value) {
    emit("update:modelValue", null);
    return;
  }

  const value = {
    countryCode: selectedCountry.value.code,
    scope: countryScope.value === SPECIFIC ? SPECIFIC : COUNTRY_WIDE,
  };

  const countryRow =
    countriesWithFlags.value.find(
      (c) => c?.code === selectedCountry.value?.code
    ) || selectedCountry.value;
  const operatingCountryId =
    countryRow?.operatingCountryId ??
    (countryRow?.catalogCountryId == null ? countryRow?.id : null) ??
    null;
  if (operatingCountryId != null) {
    value.operatingCountryId = operatingCountryId;
  }
  if (countryRow?.name || selectedCountry.value.name) {
    value.countryName = countryRow?.name || selectedCountry.value.name;
  }

  if (countryScope.value === SPECIFIC) {
    // Countries with a region level carry the region id; Romania goes straight to the
    // county / metro sub-region.
    if (hasRegionLevel.value && selectedRegion.value) {
      value.regionId = selectedRegion.value.id;
      if (selectedRegion.value.name) value.regionName = selectedRegion.value.name;
      if (selectedRegion.value.code) value.regionCode = selectedRegion.value.code;
    }

    const subRegionType = activeSubRegionType.value;
    if (subRegionType) {
      value.scope = subRegionType;
      value.subRegionType = subRegionType;
      if (selectedSubRegion.value) {
        value.subRegionId = selectedSubRegion.value.id;
        if (selectedSubRegion.value.name) value.subRegionName = selectedSubRegion.value.name;
        const parentRegionId = selectedSubRegion.value.parentRegionId;
        if (parentRegionId != null && value.regionId == null) {
          value.regionId = parentRegionId;
        }
      }
    } else if (value.regionId) {
      value.scope = REGION_WIDE;
    }
  }

  emit("update:modelValue", value);
}

function getSubRegionHint(type) {
  const config = countryConfig.value;
  if (!config) return "";

  if (config.independentScopes) {
    // Romania case: County and Metro Area are independent sibling scopes.
    return `Type to search. Switching between ${config.metroLabel} and ${config.localUnitLabel} clears the current selection.`;
  }

  // Standard case: falls back to region-wide
  const label = type === "metro" ? config.metroLabel : config.localUnitLabel;
  return `Type to search. Clearing ${label} falls back to ${config.regionLabel?.toLowerCase()}-wide.`;
}

// Applies the saved modelValue once its country is in the list, for a country catalog that
// loads after mount. Hydrating confirms the saved value, so it must not emit a change.
let hydrating = false;
function hydrateFromModel(country) {
  hydrating = true;
  selectedCountry.value = country;
  countryScope.value = getInitialCountryScope();
  regionScope.value = initialRegionScope(country);
  selectedRegion.value = toLocationOption(
    props.modelValue?.regionId,
    props.modelValue?.regionName,
    props.modelValue?.regionCode
  );
  selectedSubRegion.value = toLocationOption(
    props.modelValue?.subRegionId,
    props.modelValue?.subRegionName
  );
  nextTick(() => {
    hydrating = false;
  });
}

// Watch for modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      // Parent v-model is null but the picker still shows a country — push state up
      // instead of wiping the UI (Pay Range modal Step 1 validation reads parent ref).
      if (selectedCountry.value?.code) {
        emitValue();
        return;
      }
      selectedCountry.value = null;
      selectedRegion.value = null;
      selectedSubRegion.value = null;
      countryScope.value = COUNTRY_WIDE;
      regionScope.value = null;
      return;
    }

    if (newValue.countryCode && props.countries) {
      const country = props.countries.find((c) => c.code === newValue.countryCode);
      if (country) {
        if (!selectedCountry.value || selectedCountry.value.code !== country.code) {
          selectedCountry.value = country;
          selectedRegion.value = null;
          selectedSubRegion.value = null;
        }
        countryScope.value = getInitialCountryScope();
        regionScope.value = initialRegionScope(country);

        selectedRegion.value = toLocationOption(
          newValue.regionId,
          newValue.regionName,
          newValue.regionCode
        );
        selectedSubRegion.value = toLocationOption(
          newValue.subRegionId,
          newValue.subRegionName
        );
      }
    }
  }
);

// Watchers
watch(countryScope, (newScope) => {
  if (newScope === COUNTRY_WIDE) {
    selectedRegion.value = null;
    selectedSubRegion.value = null;
    regionScope.value = hasRegionLevel.value ? REGION_WIDE : null;
  }
  if (!hydrating) emitValue();
});

watch(regionScope, (newScope, oldScope) => {
  // County and Metro Area are independent sibling scopes, so switching between them
  // clears the current selection.
  if (newScope !== oldScope) selectedSubRegion.value = null;
  if (!hydrating) emitValue();
});

// ER-11872: when the account has exactly one operating country, auto-default the
// Country field to it (country-wide scope) and keep it selected. In edit mode a
// matching saved country is a no-op confirmation, never a change of the saved value.
watch(
  singleCountryMode,
  (isSingle) => {
    if (!isSingle) return;
    const sole = Array.isArray(props.countries) ? props.countries[0] : null;
    if (!sole) return;

    if (!selectedCountry.value || selectedCountry.value.code !== sole.code) {
      if (props.modelValue?.countryCode === sole.code) {
        hydrateFromModel(sole);
        return;
      }
      selectedCountry.value = sole;
      selectedRegion.value = null;
      selectedSubRegion.value = null;
      countryScope.value = COUNTRY_WIDE;
      regionScope.value = initialRegionScope(sole);
      emitValue();
    }
  },
  { immediate: true }
);

// Parent v-model can stay null if the first emit was missed (e.g. catalog still loading).
watch(
  () => [selectedCountry.value?.code, props.modelValue?.countryCode],
  ([selectedCode, modelCode]) => {
    if (selectedCode && !modelCode) emitValue();
  }
);

watch(
  () => props.countries,
  (list) => {
    if (!list?.length) return;
    if (!selectedCountry.value?.code) {
      const savedCode = props.modelValue?.countryCode;
      const saved = savedCode ? list.find((c) => c?.code === savedCode) : null;
      if (saved) hydrateFromModel(saved);
      return;
    }
    const code = selectedCountry.value.code;
    const match = list.find((c) => c?.code === code);
    if (match) selectedCountry.value = match;
    if (!props.modelValue?.countryCode) emitValue();
  }
);
</script>

<style scoped>
.location-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.location-picker__level {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.location-picker__scope-toggle {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.location-picker__toggle {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 2px;
  background-color: #ffffff;
  max-width: fit-content;
}

.location-picker__toggle-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: #475467 !important;
}

.location-picker__toggle-btn.v-btn--active {
  background-color: #f9f5ff !important;
  color: #6941c6 !important;
}

.location-picker__country-label {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.location-picker__required {
  color: #b42318;
}

.location-picker__hint {
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  font-weight: 400;
}
</style>
